import { useEffect, useRef, useState, type MouseEvent } from 'react';
import type { CellValue } from '../../game/board';
import type { Difficulty } from '../../game/generator';
import type { SessionAction } from '../../game/session';
import type { SudokuGameModel } from '../../app/use-sudoku-game';
import { translate } from '../../i18n/translations';
import type { Language } from '../../platform/settings/settings';
import { GameBoard } from './GameBoard';
import { ConfirmDialog } from '../dialog/ConfirmDialog';
import styles from './GameScreen.module.css';

type GameScreenProps = {
  language: Language;
  model: SudokuGameModel;
  elapsedMs: number;
  onAction: (action: SessionAction) => void;
  onNewGame: (difficulty: Difficulty) => void;
};

const difficulties: readonly Difficulty[] = ['easy', 'medium', 'hard'];
const MILLISECONDS_PER_SECOND = 1_000;
const SECONDS_PER_MINUTE = 60;

type PendingAction =
  | { kind: 'restart'; wasPlaying: boolean }
  | { kind: 'newGame'; difficulty: Difficulty; wasPlaying: boolean };

function readClockMs(): number {
  return Date.now();
}

function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.floor(elapsedMs / MILLISECONDS_PER_SECOND);
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function GameScreen({ language, model, elapsedMs, onAction, onNewGame }: GameScreenProps) {
  const [candidateMode, setCandidateMode] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const resumeRef = useRef<HTMLButtonElement>(null);
  const invokingControlRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (model.session?.status === 'paused' && pendingAction === null) {
      resumeRef.current?.focus();
    }
  }, [model.session?.status, pendingAction]);

  const hasProgress =
    model.session !== null &&
    (model.session.entries.some((value) => value !== 0) ||
      model.session.candidates.some((mask) => mask !== 0));

  const openConfirmation = (action: PendingAction, invokingControl: HTMLButtonElement): void => {
    invokingControlRef.current = invokingControl;
    setPendingAction(action);
    if (action.wasPlaying) {
      onAction({ type: 'pause', nowMs: readClockMs(), automatic: false });
    }
  };

  const requestNewGame = (difficulty: Difficulty, event: MouseEvent<HTMLButtonElement>): void => {
    const session = model.session;
    if (session !== null && hasProgress && session.status !== 'completed') {
      openConfirmation(
        { kind: 'newGame', difficulty, wasPlaying: session.status === 'playing' },
        event.currentTarget,
      );
    } else {
      onNewGame(difficulty);
    }
  };

  const cancelPendingAction = (): void => {
    const shouldResume = pendingAction?.wasPlaying === true;
    setPendingAction(null);
    if (shouldResume) {
      onAction({ type: 'resume', nowMs: readClockMs() });
    }
    queueMicrotask(() => invokingControlRef.current?.focus());
  };

  const confirmPendingAction = (): void => {
    const action = pendingAction;
    setPendingAction(null);
    if (action?.kind === 'restart') {
      onAction({ type: 'restart', nowMs: Date.now() });
    } else if (action?.kind === 'newGame') {
      onNewGame(action.difficulty);
    }
  };

  const difficultyPicker = (
    <fieldset className={styles.difficulties}>
      <legend>{translate(language, 'newGame')}</legend>
      {difficulties.map((difficulty) => (
        <button
          type="button"
          key={difficulty}
          aria-label={`${translate(language, 'newGame')}: ${translate(language, difficulty)}`}
          data-active={model.difficulty === difficulty || undefined}
          onClick={(event) => requestNewGame(difficulty, event)}
        >
          {translate(language, difficulty)}
        </button>
      ))}
    </fieldset>
  );

  if (model.phase === 'generating') {
    return (
      <section className={styles.state} aria-live="polite">
        <h2>{translate(language, 'gameTitle')}</h2>
        <p>{translate(language, 'generating')}</p>
        {difficultyPicker}
      </section>
    );
  }

  if (model.phase === 'failed') {
    return (
      <section className={styles.state} aria-live="polite">
        <h2>{translate(language, 'gameTitle')}</h2>
        <p>{translate(language, 'generationFailed')}</p>
        <button type="button" onClick={() => onNewGame(model.difficulty)}>
          {translate(language, 'tryAgain')}
        </button>
        {difficultyPicker}
      </section>
    );
  }

  const session = model.session;
  const isPlaying = session.status === 'playing';
  const sendNumber = (value: CellValue): void => {
    onAction(candidateMode ? { type: 'toggleCandidate', value } : { type: 'enterValue', value });
  };

  return (
    <section className={styles.game} aria-labelledby="game-title">
      <div className={styles.gameHeader}>
        <div>
          <p className={styles.eyebrow}>
            {translate(language, 'difficulty')}: {translate(language, session.difficulty)}
          </p>
          <h2 id="game-title">{translate(language, 'gameTitle')}</h2>
        </div>
        <time className={styles.timer} aria-label={translate(language, 'timer')}>
          {formatElapsed(elapsedMs)}
        </time>
      </div>
      <p className={styles.mistakes} aria-live="polite" aria-atomic="true">
        <span>{translate(language, 'mistakes')}</span>
        <span aria-label={translate(language, 'mistakeStatus')}>
          {session.mistakes}/{session.maxMistakes}
        </span>
      </p>

      <div className={styles.workspace}>
        <div className={styles.boardArea}>
          <GameBoard
            language={language}
            session={session}
            candidateMode={candidateMode}
            onAction={onAction}
          />
          {session.status === 'paused' ? (
            <div className={styles.pauseActions} aria-live="polite">
              <p>{translate(language, 'paused')}</p>
              <button
                className={styles.primary}
                type="button"
                ref={resumeRef}
                onClick={() => onAction({ type: 'resume', nowMs: Date.now() })}
              >
                {translate(language, 'resume')}
              </button>
            </div>
          ) : null}
          {session.status === 'completed' ? (
            <p className={styles.completed} role="status">
              {translate(language, 'completed')}
            </p>
          ) : null}
          {session.status === 'failed' ? (
            <p className={styles.failed} role="status">
              {translate(language, 'failedGame')}
            </p>
          ) : null}
        </div>

        <aside className={styles.controls}>
          {difficultyPicker}
          <div className={styles.actionRow} aria-label={translate(language, 'gameActions')}>
            <button
              type="button"
              disabled={!session.canUndo}
              onClick={() => onAction({ type: 'undo' })}
            >
              {translate(language, 'undo')}
            </button>
            <button
              type="button"
              disabled={!session.canRedo}
              onClick={() => onAction({ type: 'redo' })}
            >
              {translate(language, 'redo')}
            </button>
          </div>

          <div className={styles.numberPad} aria-label={translate(language, 'numberPad')}>
            {Array.from({ length: 9 }, (_, index) => {
              const value = (index + 1) as CellValue;
              return (
                <button
                  type="button"
                  key={value}
                  disabled={!isPlaying}
                  onClick={() => sendNumber(value)}
                >
                  {value}
                </button>
              );
            })}
          </div>

          <div className={styles.actionRow}>
            <button
              type="button"
              aria-pressed={candidateMode}
              disabled={!isPlaying}
              onClick={() => setCandidateMode((enabled) => !enabled)}
            >
              {translate(language, 'notes')}
            </button>
            <button type="button" disabled={!isPlaying} onClick={() => onAction({ type: 'clear' })}>
              {translate(language, 'erase')}
            </button>
          </div>

          <div className={styles.actionRow}>
            {isPlaying ? (
              <button
                type="button"
                onClick={() => onAction({ type: 'pause', nowMs: Date.now(), automatic: false })}
              >
                {translate(language, 'pause')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={(event) => {
                if (hasProgress && session.status !== 'completed') {
                  openConfirmation(
                    { kind: 'restart', wasPlaying: session.status === 'playing' },
                    event.currentTarget,
                  );
                } else {
                  onAction({ type: 'restart', nowMs: Date.now() });
                }
              }}
            >
              {translate(language, 'restart')}
            </button>
          </div>
        </aside>
      </div>
      {pendingAction === null ? null : (
        <ConfirmDialog
          title={translate(
            language,
            pendingAction.kind === 'restart' ? 'confirmRestartTitle' : 'confirmNewGameTitle',
          )}
          message={translate(
            language,
            pendingAction.kind === 'restart' ? 'confirmRestartMessage' : 'confirmNewGameMessage',
          )}
          cancelLabel={translate(language, 'cancel')}
          confirmLabel={translate(
            language,
            pendingAction.kind === 'restart' ? 'confirmRestartAction' : 'confirmNewGameAction',
          )}
          onCancel={cancelPendingAction}
          onConfirm={confirmPendingAction}
        />
      )}
    </section>
  );
}
